import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { authApi } from "../../services/authApi";
import { formatApiErrorDetail } from "../../lib/api";
import { useAuth } from "../../hooks/useAuth";
import { useLocalization } from "../../hooks/useLocalization";
import { getLandingRouteForUser } from "../../lib/roleRouting";

export default function ChangePasswordPage() {
  const { t } = useLocalization();
  const { refreshUser } = useAuth();
  const navigate = useNavigate();
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (newPassword !== confirmPassword) {
      setError(t("changePassword.mismatch"));
      return;
    }
    setSubmitting(true);
    try {
      await authApi.changePassword(currentPassword, newPassword);
      const updated = await refreshUser();
      navigate(getLandingRouteForUser(updated), { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="w-full max-w-sm" data-testid="change-password-page">
      <h2 className="text-2xl font-bold tracking-tight text-gray-900 mb-2">{t("changePassword.title")}</h2>
      <p className="text-sm text-gray-500 mb-6">{t("changePassword.mustChangeMessage")}</p>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="current-password" className="sr-only">{t("changePassword.currentPassword")}</label>
          <input
            id="current-password"
            data-testid="current-password-input"
            type="password"
            required
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            placeholder={t("changePassword.currentPassword")}
            className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
          />
        </div>
        <div>
          <label htmlFor="new-password" className="sr-only">{t("changePassword.newPassword")}</label>
          <input
            id="new-password"
            data-testid="new-password-input"
            type="password"
            required
            minLength={8}
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            placeholder={t("changePassword.newPassword")}
            className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
          />
        </div>
        <div>
          <label htmlFor="confirm-new-password" className="sr-only">{t("changePassword.confirmNewPassword")}</label>
          <input
            id="confirm-new-password"
            data-testid="confirm-new-password-input"
            type="password"
            required
            minLength={8}
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            placeholder={t("changePassword.confirmNewPassword")}
            className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
          />
        </div>
        {error && <p className="text-sm text-red-500" data-testid="change-password-error">{error}</p>}
        <button
          data-testid="change-password-submit-button"
          type="submit"
          disabled={submitting}
          className="w-full py-3 rounded-full bg-blue-600 text-white font-semibold hover:bg-blue-700 active:scale-95 transition-all disabled:opacity-50"
        >
          {submitting ? t("common.loading") : t("changePassword.submit")}
        </button>
      </form>
    </div>
  );
}
